import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Helper to normalize and join URL paths
const joinUrl = (base, path) => {
  if (!base) return path;
  let normalizedBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${normalizedBase}/${normalizedPath}`;
};

// Robust fetch with retry and better error reporting
const robustFetch = async (url, options = {}, retries = 2) => {
  try {
    const response = await fetch(url, {
      mode: 'cors',
      credentials: 'omit',
      ...options,
      headers: {
        ...options.headers,
      }
    });
    
    if (!response.ok) {
      let errorDetail = response.statusText;
      try {
        const data = await response.json();
        errorDetail = data.detail || data.message || JSON.stringify(data);
      } catch (e) {}
      throw new Error(`HTTP ${response.status}: ${errorDetail}`);
    }
    return response;
  } catch (err) {
    if (retries > 0 && err.name !== 'AbortError') {
      console.warn(`Fetch failed, retrying... (${retries} left). Error: ${err.message}`);
      await new Promise(res => setTimeout(res, 1000));
      return robustFetch(url, options, retries - 1);
    }
    throw err;
  }
};

function Validation() {
  const navigate = useNavigate();
  const [jobId, setJobId] = useState('');
  const [jobs, setJobs] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollingRef = useRef({});

  const [config] = useState(() => {
    const savedConfig = localStorage.getItem('scada_api_config');
    const parsed = savedConfig ? JSON.parse(savedConfig) : {};
    return {
      apiBase: parsed.apiUrl || process.env.REACT_APP_API_BASE || 'http://localhost:20021/api',
      apiKey: parsed.apikey !== undefined ? parsed.apikey : (process.env.REACT_APP_API_KEY || '')
    };
  });

  const getHeaders = useCallback(() => {
    const headers = {};
    if (config.apiKey) headers['apikey'] = config.apiKey;
    return headers;
  }, [config.apiKey]);

  const fetchJobStatus = useCallback(async (id) => {
    try {
      const response = await robustFetch(joinUrl(config.apiBase, `status/${id}`), {
        headers: getHeaders()
      });
      const data = await response.json();
      setJobs(prev => ({ ...prev, [id]: data }));
      return data.status;
    } catch (err) {
      console.error(`Status fetch error for ${id}:`, err);
      return null;
    }
  }, [config.apiBase, getHeaders]);

  const pollJobStatus = useCallback((id) => {
    if (pollingRef.current[id]) {
      clearInterval(pollingRef.current[id]);
    }
    
    pollingRef.current[id] = setInterval(async () => {
      const status = await fetchJobStatus(id);
      if (status === 'completed' || status === 'failed') {
        clearInterval(pollingRef.current[id]);
        delete pollingRef.current[id];
      }
    }, 2000);
  }, [fetchJobStatus]);

  const fetchAllJobs = useCallback(async () => {
    try {
      const response = await robustFetch(joinUrl(config.apiBase, 'all_jobs/'), {
        headers: getHeaders()
      });
      const data = await response.json();
      const jobsMap = {};
      data.forEach(job => {
        jobsMap[job.job_id] = job;
      });
      setJobs(jobsMap);
      
      data.forEach(job => {
        if (job.status !== 'completed' && job.status !== 'failed') {
          pollJobStatus(job.job_id);
        }
      });
    } catch (err) {
      console.error('Fetch all jobs error:', err);
      setError(`Failed to sync with server: ${err.message}`);
    }
  }, [config.apiBase, getHeaders, pollJobStatus]);

  useEffect(() => {
    fetchAllJobs();
    return () => {
      Object.values(pollingRef.current).forEach(interval => clearInterval(interval));
    };
  }, [fetchAllJobs]);

  const handleSubmit = async (e, manualId = null, jobType = null) => {
    if (e) e.preventDefault();
    const targetId = manualId || jobId;
    if (!targetId.trim()) {
      setError('Please enter a job ID');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await robustFetch(joinUrl(config.apiBase, `validate_job/${encodeURIComponent(targetId)}`), {
        method: 'POST',
        headers: getHeaders()
      });

      const data = await response.json();
      console.log('Validation result:', data);
      setResult({...data, _isUdt: jobType === 'udt'});
      // Scroll to result
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    } catch (err) {
      setError(`Network error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getTypeBadge = (type) => {
    const isUdt = type === 'udt';
    return (
      <span style={{ 
        padding: '0.1rem 0.5rem', 
        borderRadius: '12px', 
        fontSize: '0.65rem', 
        fontWeight: '700',
        background: isUdt ? '#e0e7ff' : '#f1f5f9',
        color: isUdt ? '#4338ca' : '#475569',
        textTransform: 'uppercase',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 'normal',
        border: `1px solid ${isUdt ? '#c7d2fe' : '#e2e8f0'}`,
        height: '1.2rem'
      }}>
        {type || 'tag'}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const styles = {
      queued: { bg: '#fef3c7', color: '#92400e' },
      processing: { bg: '#dbeafe', color: '#1e40af' },
      completed: { bg: '#d1fae5', color: '#065f46' },
      failed: { bg: '#fee2e2', color: '#991b1b' }
    };
    const style = styles[status] || styles.queued;
    return (
      <span style={{ 
        padding: '0.35rem 0.75rem', 
        borderRadius: '20px', 
        fontSize: '0.75rem', 
        fontWeight: '600',
        background: style.bg,
        color: style.color
      }}>
        {status}
      </span>
    );
  };

  return (
    <div>
      <div className="back-button" onClick={() => navigate('/')}>
        ← Back to Home
      </div>

      <div className="page-header">
        <h2 className="page-title">Validation Agent</h2>
        <p className="page-subtitle">
          Validate converted output by comparing source Excel with JSON
        </p>
      </div>

      <div className="card">
        <h3 className="card-title">Manual Validation</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Job ID</label>
            <input
              type="text"
              value={jobId}
              onChange={(e) => setJobId(e.target.value)}
              placeholder="Enter the job ID from Tag Conversion"
            />
          </div>
          
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Validating...' : 'Validate Manual ID'}
          </button>
        </form>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 className="card-title" style={{ margin: 0 }}>Conversion Jobs</h3>
          <button className="btn btn-secondary btn-sm" onClick={fetchAllJobs} disabled={loading}>
            Refresh
          </button>
        </div>
        
        {Object.keys(jobs).length === 0 ? (
          <p style={{ color: '#64748b', textAlign: 'center', padding: '2rem' }}>
            No recent jobs found.
          </p>
        ) : (
          <div className="job-list">
            {Object.entries(jobs)
              .sort(([, a], [, b]) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
              .filter(([, job]) => job.status !== 'failed')
              .map(([id, job]) => (
                <div key={id} className="job-item">
                  <div className="job-info">
                    <div className="job-filename" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      {job.filename || 'Unknown'}
                      {getTypeBadge(job.tag_type)}
                    </div>
                    <div className="job-meta">
                      ID: {id} • {job.created_at ? new Date(job.created_at).toLocaleString() : 'N/A'}
                      {job.file_size && ` • ${(parseInt(job.file_size) / 1024).toFixed(1)} KB`}
                    </div>
                  </div>
                  <div className="job-actions">
                    {getStatusBadge(job.status)}
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={(e) => handleSubmit(e, id, job.tag_type)}
                      disabled={loading || job.status !== 'completed'}
                    >
                      Verify
                    </button>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          Validating tags...
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          <div className="alert-content">
            <div className="alert-title">Error</div>
            <div>{error}</div>
          </div>
        </div>
      )}

      {result && (
        <div className="card" id="validation-result">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="card-title" style={{ margin: 0 }}>
              {result._isUdt ? 'UDT' : 'Tag'} Validation Results
            </h3>
            <span style={{ fontSize: '0.8rem', color: '#64748b', background: '#f1f5f9', padding: '0.25rem 0.6rem', borderRadius: '4px', fontWeight: '500' }}>
              Job ID: {result.job_id}
            </span>
          </div>
          
          {result._isUdt ? (
            <>
              {/* UDT Validation Display */}
              <div className="stats">
                <div className="stat-card">
                  <div className="stat-value">{result.excel_count}</div>
                  <div className="stat-label">WonderWare Tags</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{result.json_count}</div>
                  <div className="stat-label">Ignition Tags</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{result.matched}</div>
                  <div className="stat-label">Matched</div>
                </div>
              </div>

              <div style={{ marginTop: '1.5rem' }}>
                <h4 style={{ color: '#0f766e', marginBottom: '0.75rem' }}>UDT Instances</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  {/* Excel Instances by Type */}
                  <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontWeight: '600', marginBottom: '0.75rem', color: '#334155' }}>Wonderware</div>
                    {(() => {
                      let instances = result.excel_instances;
                      if (typeof instances === 'string') {
                        try { instances = JSON.parse(instances); } catch { instances = {}; }
                      }
                      const entries = instances ? Object.entries(instances) : [];
                      if (entries.length === 0) {
                        return <div style={{ color: '#64748b' }}>No instances</div>;
                      }
                      return entries.map(([udtType, count]) => (
                        <div key={udtType} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', borderBottom: '1px solid #e2e8f0' }}>
                          <span style={{ color: '#475569' }}>{udtType}</span>
                          <span style={{ fontWeight: '600', color: '#0f172a' }}>{count}</span>
                        </div>
                      ));
                    })()}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0 0', marginTop: '0.5rem', borderTop: '2px solid #cbd5e1', fontWeight: '700' }}>
                      <span>Total</span>
                      <span>{result.excel_instances_total}</span>
                    </div>
                  </div>
                  
                  {/* JSON Instances by Type */}
                  <div style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontWeight: '600', marginBottom: '0.75rem', color: '#334155' }}>Ignition</div>
                    {(() => {
                      let instances = result.json_instances;
                      if (typeof instances === 'string') {
                        try { instances = JSON.parse(instances); } catch { instances = {}; }
                      }
                      const entries = instances ? Object.entries(instances) : [];
                      if (entries.length === 0) {
                        return <div style={{ color: '#64748b' }}>No instances</div>;
                      }
                      return entries.map(([udtType, count]) => (
                        <div key={udtType} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', borderBottom: '1px solid #e2e8f0' }}>
                          <span style={{ color: '#475569' }}>{udtType}</span>
                          <span style={{ fontWeight: '600', color: '#0f172a' }}>{count}</span>
                        </div>
                      ));
                    })()}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0 0', marginTop: '0.5rem', borderTop: '2px solid #cbd5e1', fontWeight: '700' }}>
                      <span>Total</span>
                      <span>{result.json_instances_total}</span>
                    </div>
                  </div>
                </div>
                
                {/* Match Status */}
                <div style={{ marginTop: '1rem', textAlign: 'center', padding: '0.75rem', borderRadius: '8px', background: result.instances_match ? '#d1fae5' : '#fee2e2', color: result.instances_match ? '#065f46' : '#991b1b', fontWeight: '600' }}>
                  {result.instances_match ? '✓ All UDT instances matched' : '✗ Instance count mismatch'}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Regular Tag Validation Display */}
              <div className="stats">
                <div className="stat-card">
                  <div className="stat-value">{result.csv_count}</div>
                  <div className="stat-label">WonderWare Tags</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{result.json_count}</div>
                  <div className="stat-label">Ignition Tags</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{result.matched}</div>
                  <div className="stat-label">Matched</div>
                </div>
              </div>
            </>
          )}

          {(result.missing && result.missing.length > 0) && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ color: '#ef4444', marginBottom: '0.75rem' }}>
                {result._isUdt ? 'Missing UDT Tags' : 'Missing Tags'} ({result.missing.length})
              </h4>
              <div className="result-box">
                {result.missing.map((tag, index) => (
                  <div key={index}>{JSON.stringify(tag)}</div>
                ))}
              </div>
            </div>
          )}

          {(result.extra && result.extra.length > 0) && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ color: '#6366f1', marginBottom: '0.75rem' }}>
                {result._isUdt ? 'Extra UDT Tags' : 'Extra Tags'} ({result.extra.length})
              </h4>
              <div className="result-box">
                {result.extra.map((tag, index) => (
                  <div key={index}>{JSON.stringify(tag)}</div>
                ))}
              </div>
            </div>
          )}

          {result.missing?.length === 0 && result.extra?.length === 0 && (
            <div className="alert alert-success" style={{ marginTop: '1.5rem' }}>
              {result._isUdt ? 'All UDT tags and instances matched successfully!' : 'All tags matched successfully!'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Validation;
